import { Util } from '@libs/utilities/util';

import { IPodDesign } from '../../contracts/pod-design.interface';
import { IDisplateOptions, IDisplateText } from '../../contracts/pod-listing.interface';
import { EnumPlatform } from '../../contracts/print-on-demand.constants';
import { IUploadPodListingLimitations, UPLOAD_CONFIG } from '../../upload.config';
import { IListingCheck, IPlatformStatus, PLACEHOLDERS } from '../data/listing-check.interface';


export class DisplateListingCheckHelper implements IListingCheck {

	//** Configurations */
	private readonly LISTING_LIMITATIONS: IUploadPodListingLimitations = UPLOAD_CONFIG.POD.LISTING_LIMITATIONS;


	/**------------------------------------------------------
	 * Check for empty listing data
	 */
	isListingEmpty(data: IPodDesign): boolean {

		//0 - is any displate data available
		if (data?.listing?.displate === null || typeof data?.listing?.displate === 'undefined') return true;

		//1 - is any listing text available
		const textData   : IDisplateText 	= data.listing.displate.text;
		const optionData : IDisplateOptions = data.listing.displate.options;
		const textValues : string[] 		= [textData.title, textData.description, textData.keywords.join(''), optionData.collection, optionData.categories.join('')];
		if (Util.String.areEmpty(textValues)) return true;

		//2 - listing data was available
		return false;
	}


	/**------------------------------------------------------
	 * Displate
	 * --------
	 * Title
	 *  - required, max 26 char
	 * Description
	 *  - required, max 140 char
	 * Keywords
	 *  - required, min 5 keywords, max 20 keywords
	 * Collection
	 *  - required, max 16 char
	 * Categories
	 *  - required, min/max 1 category
	 */
	checkListing(data: IPodDesign): IPlatformStatus {

		//0 - check if any displate data was defined
		const status: IPlatformStatus = { code: EnumPlatform.Displate, warnings: [], errors : [] };
		if (this.isListingEmpty(data) || !data.listing.displate) {
			status.errors.push('displate listing is empty');
			return status;
		}

		//1 - check for texts in listing
		const text				: IDisplateText = data.listing.displate.text;
		const isTitleEmpty		: boolean 		= Util.String.isEmpty(text.title);
		const isDescriptionEmpty: boolean 		= Util.String.isEmpty(text.description);
		const isKeywordsEmpty	: boolean 		= Util.Array.isEmpty(text.keywords);

		//2 - check for any placeholders in the text
		if (!isTitleEmpty && !isDescriptionEmpty && !isKeywordsEmpty) {
			for (const placeholder of PLACEHOLDERS) {
				if (text.title.includes(placeholder)) 		status.warnings.push(`placeholder "${placeholder}" found in title (en)`);
				if (text.description.includes(placeholder)) status.warnings.push(`placeholder "${placeholder}" found in description (en)`);
				if (text.keywords.includes(placeholder)) 	status.warnings.push(`placeholder "${placeholder}" found in keywords (en)`);
			}
		}

		//3 - check title
		if (isTitleEmpty) status.errors.push('title is empty (en)');
		if (text.title.length > this.LISTING_LIMITATIONS.DISPLATE.TITLE) status.warnings.push(`title over ${this.LISTING_LIMITATIONS.DISPLATE.TITLE} characters (en)`);

		//4 - check description
		if (isDescriptionEmpty)	status.errors.push('description is empty (en)');
		if (text.description.length > this.LISTING_LIMITATIONS.DISPLATE.DESCRIPTION) status.warnings.push(`description over ${this.LISTING_LIMITATIONS.DISPLATE.DESCRIPTION} characters (en)`);

		//5 - check keywords
		if (text.keywords.length < 5) status.errors.push('less than 5 keywords (en)');
		if (text.keywords.length > this.LISTING_LIMITATIONS.DISPLATE.KEYWORDS) status.warnings.push(`over ${this.LISTING_LIMITATIONS.DISPLATE.KEYWORDS} keywords (en)`);

		//6 - check options
		this.checkOptionsInListing(data.listing.displate.options, status);

		//7 - return status
		return status;
	}

	/**------------------------------------------------------
	 * Helper Function
	 */
	private checkOptionsInListing(options: IDisplateOptions, status: IPlatformStatus) {

		//0 - check collection
		if (Util.String.isEmpty(options.collection)) status.errors.push('collection is empty (en)');
		if (options.collection.length > this.LISTING_LIMITATIONS.DISPLATE.COLLECTION) status.warnings.push(`collection over ${this.LISTING_LIMITATIONS.DISPLATE.COLLECTION} characters (en)`);

		//1 - are there special chars in the collection
		const specialCharCheck: RegExp = /[-.,_#@()%${}§'"*<>]/;
		if (specialCharCheck.test(options.collection)) status.errors.push('collection contains special chars (en)');

		//2 - check categories
		if (options.categories.length === 0) status.errors.push('no categories provided (en)');
		if (options.categories.length > this.LISTING_LIMITATIONS.DISPLATE.CATEGORIES) status.warnings.push(`over ${this.LISTING_LIMITATIONS.DISPLATE.CATEGORIES} categories (en)`);

		//3 - was any background color configured
		if (!Util.Color.isHexColor(options.background)) status.errors.push('missing background color');
	}
}
